import styled, { css } from 'styled-components';
import { Hint } from '@getgrover/ui';
import { Input } from 'ggApp/shared/components/input';

import omitProps from 'ggApp/utils/omitProps';
import FadeInAnimation from 'ggApp/shared/common/Animations/FadeIn';
import NavigationLink from 'ggApp/shared/common/NavigationLink';
import CommonCheckbox from 'ggApp/shared/common/Checkbox';

import {
    Paragraph,
    SmallCapsHeadline,
    SubHeadline,
    TinyParagraph,
} from 'ggApp/shared/common/Typography';

import GeneralStyledComponent from './GeneralStyledComponent';

export const AddressInput = styled(Input)`
   ${({ active }) =>
        active &&
        css`
          span {
            top: 0px;
          }
        `}
`

export const FormWrapper = styled.form`
    display: flex;
    flex-grow: 1;
    flex-shrink: 0;
    flex-direction: column;
    align-items: stretch;
    padding: ${({ address2FAStyle }) => address2FAStyle ? '0px 16px ' : '0 24px 24px'};

    @media (max-width: 375px) {
        padding: 0 16px 16px;
    }

    animation-fill-mode: forwards;
    ${FadeInAnimation};

    ${({ illustration }) =>
        !illustration &&
        css`
            padding-top: 8px;
        `};
`;

export const Heading = styled(SubHeadline.withComponent('h4'))`
    display: flex;
    align-items: center;
`;

export const HeadingEmoji = styled.span`
    display: inline;
    margin-left: 8px;
`;

export const SubHeading = styled.h5`
    ${({ theme }) => css(theme.typography.Paragraph)};
    color: ${(props) => props.theme.colors['Level 5']};
`;

export const Tip = styled(Paragraph.withComponent('span'))`
    color: ${(props) => props.theme.colors['Level 5']};
    font-style: italic;
    font-weight: bold;
    line-height: 23px;

    display: flex;
    align-items: baseline;
    position: relative;

    &::before {
        content: '?';
        display: flex;
        justify-content: center;
        align-items: center;
        flex-grow: 0;
        flex-shrink: 0;
        width: 1em;
        height: 1em;
        margin-right: 12px;
        border-radius: 50%;
        background-color: ${(props) => props.theme.colors['Level 4']};
        font-style: normal;
        color: #fff;
    }
`;

const BASE_GRID_STEP = 8;

export const FormRow = styled(
    omitProps('alignToBottom', 'justifyContent', 'paddingTop', 'paddingBottom', 'flexWrap')('div'),
)`
    width: 100%;
    display: flex;
    padding-top: ${({ paddingTop = 1, address2FAStyle }) => address2FAStyle ? '0px' : `${paddingTop * BASE_GRID_STEP}px`};
    padding-bottom: ${({ paddingBottom = 1 }) => paddingBottom * BASE_GRID_STEP}px;
    justify-content: ${({ justifyContent }) => justifyContent};
    flex-wrap: ${({ flexWrap }) => flexWrap};

    ${({ alignToBottom }) =>
        alignToBottom &&
        css`
            margin: auto auto 0;
            justify-content: space-between;
            display: flex;
        `}
`;

export const ButtonRow = styled(omitProps('alignToBottom')('div'))`
    width: 100%;
    padding-top: ${({ paddingTop = 1 }) => paddingTop * BASE_GRID_STEP}px;
    display: flex;

    > button {
        flex-basis: 0;
        flex-grow: 1;
        margin-right: 8px;
    }

    > button:last-child {
        margin-right: 0 !important;
    }

    ${({ alignToBottom }) =>
        alignToBottom &&
        css`
            margin: auto auto 0;
            justify-content: space-between;
            display: flex;
        `}
`;

export const FormColumn = styled(omitProps('width')('div'))`
    width: ${({ width }) => (typeof width === 'number' ? `${width}px` : width)};
    padding: 0 ${BASE_GRID_STEP}px;
    > label {
        width: 100%;
    }
    &:first-child {
        padding: 0 ${BASE_GRID_STEP}px 0 0;
    }

    &:last-child {
        padding: 0 0 0 ${BASE_GRID_STEP}px;
    }
`;

export const FormColumnFull = styled(omitProps('width')('div'))`
    width: 100%;
    > label {
        width: 100%;
    }
`;

export const Note = styled(TinyParagraph.withComponent('span'))`
    font-size: 10px;
    line-height: 14px;
    color: ${(props) => props.theme.colors['Level 4']};

    a {
        cursor: pointer;
        text-decoration: underline;
        color: inherit;

        :active,
        :visited {
            color: inherit;
        }
    }
`;

export const NoteLink = styled(TinyParagraph.withComponent('a'))`
    cursor: pointer;
    color: ${({ theme }) => theme.colors['Level 4']};
    padding-top: ${({ theme }) => theme.spacers.L};
    text-decoration: underline;

    :hover,
    :active,
    :visited {
        color: inherit;
    }
`;

export const IconWrapper = styled.div`
    flex-shrink: 0;
`;

export const SignInText = styled(Paragraph)`
    color: ${(props) => props.theme.colors['Level 5']};
    font-weight: bold;

    display: inline;
`;

export const SignInButton = styled.button.attrs({
    type: 'button',
})`
    font: inherit;
    color: ${({ theme }) => theme.colors['Level 5']};
    cursor: pointer;
    text-decoration: underline;

    margin-left: 4px;
`;

export const ForgotPasswordButton = styled.button.attrs({
    type: 'button',
})`
    color: ${({ theme }) => theme.colors['Level 4']};
    ${(props) => css(props.theme.typography.TinyParagraph)};
    font-weight: bold;
    font-style: italic;
    cursor: pointer;
    text-decoration: underline;
`;

export const BaseErrorMessage = styled(TinyParagraph.withComponent('span'))`
    font-weight: bold;
    color: #f44336;
`;

export const InputLabel = styled(SmallCapsHeadline.withComponent('label'))`
    width: 100%;
    color: ${(props) => props.theme.colors['Level 4']};
`;

export const InputGroup = styled.div`
    width: 100%;
    overflow: hidden;
    display: flex;
`;

export const InputGroupItem = styled(omitProps('flexGrow', 'flexShrink')(GeneralStyledComponent))`
    margin-left: 8px;
    margin-right: 8px;
    flex-shrink: 0;
    flex-grow: 0;

    :first-child {
        margin-left: unset;
    }

    :last-child {
        margin-right: unset;
    }

    ${(props) =>
        props.flexGrow &&
        css`
            flex-grow: 1;
        `}

    ${(props) =>
        props.flexShrink &&
        css`
            flex-shrink: 1;
        `}
`;

export const InfoText = styled(Paragraph.withComponent('span'))`
    ${(props) => css(props.theme.typography.Paragraph)};
    font-size: 16px;
    font-weight: 500;
    font-style: normal;
    line-height: 1.5;
`;

export const GreyText = styled(TinyParagraph.withComponent('span'))`
    color: ${({ theme }) => theme.colors['Level 4']};
    font-size: 12px;
    font-weight: 500;
`;

export const Checkbox = styled(CommonCheckbox)``;

export const Row = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
`;

export const StyledGreyLink = styled(NoteLink)`
    padding-top: 0;
    padding-left: 0.2em;
`;

export const StyledNote = styled(Note)`
    font-size: 12px;
    padding-top: ${({ theme }) => theme.spacers.L};
`;

export const StyledNoteLink = styled(NoteLink).attrs({
    target: '_blank',
    rel: 'noopener noreferer',
})`
    padding-top: ${({ theme }) => theme.spacers.L};
`;

export const StepHint = styled(omitProps('marginTop', 'marginBottom')(Hint))(
    (props) => css`
        margin-top: ${props.marginTop || 0}px;
        margin-bottom: ${props.marginBottom || 0}px;
    `,
);

export const HintLink = styled(TinyParagraph.withComponent('button')).attrs({ type: 'button' })`
    cursor: pointer;
    text-decoration: underline;
    color: inherit;

    :hover {
        color: inherit;
    }
`;

export const StyledParagraph = styled(Paragraph)`
    color: ${({ theme }) => theme.colors['Level 5']};
`;

export const SmallButtonRow = styled(ButtonRow)`
    width: auto;
    margin: 0 auto auto 0;
`;

export const ButtonLink = styled.button`
    ${({ theme }) => theme.typography.TinyParagraph};
    color: ${({ theme }) => theme.colors['Level 4']};
    text-decoration: underline;
    cursor: pointer;
`;

export const Text = styled.p`
    ${({ theme }) => theme.typography.Paragraph};
    color: ${({ theme }) => theme.colors['Level 5']};
    padding-bottom: ${({ theme }) => theme.spacers.L};
`;

export const TextWrap = styled(FormRow)`
    align-items: flex-start;
    flex-direction: column;
`;

export const StyledButtonRow = styled(ButtonRow)`
    width: auto;
    margin: auto auto 0 0;
`;

export const BenefitsHintLink = styled(NavigationLink)`
    ${({ theme }) => css(theme.typography.TinyParagraph)};
    cursor: pointer;
    text-decoration: underline;
    color: inherit;

    :hover,
    :active,
    :visited {
        color: inherit;
    }
`;

export const StyledHint = styled(Hint)`
    background-color: ${({ theme }) => theme.colors.secondaryBackground};
`;

const InputErrorClass = 'passwordInputError';
export const PassInput = styled(Input).attrs({
    classNames: {
        error: InputErrorClass,
    },
})`
    & .${InputErrorClass} {
        height: auto;
        min-height: 16px;
    }
`;

export const HelpLink = styled.div`
    ${({ theme }) => theme.typography.TinyParagraph};
    color: ${({ theme }) => theme.colors['Level 4']};
    cursor: pointer;
    text-decoration: underline;
`;

export const TextLink = styled.a`
    cursor: pointer;
    text-decoration: underline;
    color: inherit;

    :hover,
    :active,
    :visited {
        color: inherit;
    }
`;

export const InlineLink = styled.span`
    text-decoration: underline;
    cursor: pointer;
`;

export const IndividualVerificationWarning = styled.p`
    ${({ theme }) => theme.typography.TinyParagraph};
    color: ${({ theme }) => theme.colors['Level 5']};
    min-height: 52px;
    opacity: 0;
    transition: opacity 0.2s ease-in;

    &.visible {
        opacity: 1;
    }
`;
